import { MdAddChart } from 'react-icons/md';
import { RiHomeLine } from 'react-icons/ri';
import {
    TbArchive,
    TbArrowRight,
    TbBook2,
    TbFilePlus,
    TbFiles,
    TbFileText,
    TbGift,
    TbLifebuoy,
    TbLink,
    TbLogout,
    TbPalette,
    TbPencil,
    TbPlus,
    TbSearch,
    TbSpeakerphone,
    TbSquareRoundedPlus2,
    TbTrash,
    TbUserCog,
    TbUserPlus,
    TbUsersPlus,
    TbWorldWww,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import {
    globalCreationShortcuts,
    navigationRouteShortcuts,
    openNotionFormModal,
    WorkspaceFolderGlyph,
    WorkspaceResourceGlyph,
} from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemoStore,
    usePipelineStore,
    useTeamStore,
} from '@/stores';
import { Command, SetStateMethod } from '@/types';
import { optionKey, shiftKey } from '@/utilities';

import { useAntDesignTheme } from './useAntDesignTheme';
import { useBillingSnapshot } from './useBillingSnapshot';
import { useCASL } from './useCASL';
import { useWorkspaceResourceCreateAccess, WorkspaceCreatableResource } from './useWorkspaceResourceCreateAccess';

type Options = {
    setOpen: SetStateMethod<boolean>;
    setSelectedKey: SetStateMethod<string>;
    setCreationModalOpen: SetStateMethod<boolean>;
};

export function useGlobalCommandsRegistry({ setOpen, setSelectedKey, setCreationModalOpen }: Options): Command[] {
    const navigate = useNavigate();

    const { createMemo, memos } = useMemoStore();
    const { dashboards } = useDashboardStore();
    const { folders } = useFolderStore();
    const { investments } = useInvestmentStore();
    const { lists } = useListStore();
    const { pipelines } = usePipelineStore();
    const { teams } = useTeamStore();

    const { themeName, setTheme, themeOptions } = useAntDesignTheme();
    const selectableThemeOptions = themeOptions.filter(({ disabled }) => !disabled);
    const currentTheme = themeOptions.find(({ name }) => name === themeName);
    const currentIndex = selectableThemeOptions.findIndex(({ name }) => name === themeName);
    const nextIndex = (currentIndex + 1) % selectableThemeOptions.length;
    const nextTheme = selectableThemeOptions[nextIndex];
    const canChangeTheme = Boolean(nextTheme && nextTheme.name !== themeName);

    const { relationsEnabled } = useBillingSnapshot();
    const { canInviteUsers } = useCASL();
    const createFolderAccess = useWorkspaceResourceCreateAccess('folders', folders?.length ?? 0);
    const createInvestmentAccess = useWorkspaceResourceCreateAccess('investments', investments?.length ?? 0);
    const createMemoAccess = useWorkspaceResourceCreateAccess('memos', memos?.length ?? 0);
    const createPipelineAccess = useWorkspaceResourceCreateAccess('pipelines', pipelines?.length ?? 0);
    const createDashboardAccess = useWorkspaceResourceCreateAccess('dashboards', dashboards?.length ?? 0);
    const createListAccess = useWorkspaceResourceCreateAccess('lists', lists?.length ?? 0);
    const createTeamAccess = useWorkspaceResourceCreateAccess('teams', teams?.length ?? 0);
    const createAccessByResource: Record<
        WorkspaceCreatableResource,
        ReturnType<typeof useWorkspaceResourceCreateAccess>
    > = {
        folders: createFolderAccess,
        investments: createInvestmentAccess,
        memos: createMemoAccess,
        pipelines: createPipelineAccess,
        dashboards: createDashboardAccess,
        lists: createListAccess,
        teams: createTeamAccess,
    };

    const createCommandDisabledProps = (
        resource: WorkspaceCreatableResource,
    ): Pick<Command, 'disabled' | 'disabledReason'> => {
        const access = createAccessByResource[resource];

        return {
            disabled: access.disabled,
            disabledReason: access.disabledReason,
        };
    };

    const commands: Array<Command | false> = [
        // Navigation
        {
            id: 'home',
            label: 'Home',
            icon: <RiHomeLine size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.home.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.home.path);
            },
        },
        {
            id: 'inbox',
            label: 'Inbox',
            icon: <TbFiles size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.inbox.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.inbox.path);
            },
        },
        {
            id: 'folders',
            label: 'Folders',
            icon: <WorkspaceResourceGlyph resource='folders' size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.folders.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);

                navigate(navigationRouteShortcuts.folders.path);
            },
        },
        {
            id: 'search',
            label: 'Search',
            icon: <TbSearch size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.search.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.search.path);
            },
        },
        {
            id: 'investments',
            label: 'Investments',
            icon: <WorkspaceResourceGlyph resource='investment' size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.investments.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.investments.path);
            },
        },
        {
            id: 'memos',
            label: 'Memos',
            icon: <WorkspaceResourceGlyph resource='memo' size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.memos.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.memos.path);
            },
        },
        {
            id: 'pipelines',
            label: 'Pipelines',
            icon: <WorkspaceResourceGlyph resource='pipeline' size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.pipelines.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.pipelines.path);
            },
        },
        // Lifecycles remains dormant until the product surface is ready to ship.
        {
            id: 'dashboards',
            label: 'Dashboards',
            icon: <WorkspaceResourceGlyph resource='dashboard' size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.dashboards.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.dashboards.path);
            },
        },
        {
            id: 'lists',
            label: 'Lists',
            icon: <WorkspaceResourceGlyph resource='list' size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.lists.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.lists.path);
            },
        },
        {
            id: 'tasks',
            label: 'Tasks',
            icon: <WorkspaceResourceGlyph resource='task' size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.tasks.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.tasks.path);
            },
        },
        {
            id: 'teams',
            label: 'Teams',
            icon: <WorkspaceResourceGlyph resource='team' size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'navigation',
            shortcut: <>{navigationRouteShortcuts.teams.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.teams.path);
            },
        },

        // Search Workspace
        // {
        //     id: 'searchCompanies',
        //     label: 'Search Companies',
        //     icon: <TbSearch size='1rem' style={{  marginRight: '0.375rem' }} />,
        //     group: 'searchWorkspace',
        //     onSelect: () => {
        //         setOpen(false);
        //         setSelectedKey('createInvestment');
        //         setCreationModalOpen(true);
        //     },
        // },
        // {
        //     id: 'searchDocuments',
        //     label: 'Search Documents',
        //     icon: <TbFileSearch size='1rem' style={{  marginRight: '0.375rem' }} />,
        //     group: 'searchWorkspace',
        //     onSelect: () => {
        //         setOpen(false);
        //         setSelectedKey('createInvestment');
        //         setCreationModalOpen(true);
        //     },
        // },
        // {
        //     id: 'searchInvestments',
        //     label: 'Search Investments',
        //     icon: (
        //         <TbAlignLeft
        //             size='1rem'
        //             style={{  marginRight: '0.375rem', transform: 'rotate(-90deg)' }}
        //         />
        //     ),
        //     group: 'searchWorkspace',
        //     onSelect: () => {
        //         setOpen(false);
        //         setSelectedKey('createInvestment');
        //         setCreationModalOpen(true);
        //     },
        // },
        // {
        //     id: 'searchMemo',
        //     label: 'Search Memos',
        //     icon: <TbFilePlus size='1rem' style={{  marginRight: '0.375rem' }} />,
        //     group: 'searchWorkspace',
        //     onSelect: () => {
        //         setOpen(false);
        //         createMemo({ title: 'Untitled' }).then((m) => {
        //             toast('Memo Created', {
        //                 icon: <TbFilePlus size='1rem' />,
        //                 description: m?.title,
        //             });

        //             navigate(`/memos/${m?.id}`);
        //         });
        //     },
        // },
        // {
        //     id: 'searchPipeline',
        //     label: 'Search Pipelines',
        //     icon: <TbPlus size='1rem' style={{  marginRight: '0.375rem' }} />,
        //     group: 'searchWorkspace',
        //     onSelect: () => {
        //         setOpen(false);
        //         setSelectedKey('createPipeline');
        //         setCreationModalOpen(true);
        //     },
        // },
        // {
        //     id: 'searchTeam',
        //     label: 'Search Teams',
        //     icon: <TbUsersPlus size='1rem' style={{  marginRight: '0.375rem' }} />,
        //     group: 'searchWorkspace',
        //     onSelect: () => {
        //         setOpen(false);
        //         setSelectedKey('createTeam');
        //         setCreationModalOpen(true);
        //     },
        // },

        // Quick Actions
        {
            id: 'createFolder',
            label: 'Create Folder',
            icon: <WorkspaceFolderGlyph size='1rem' style={{ marginRight: '0.375rem' }} variant='create' />,
            group: 'quickActions',
            ...createCommandDisabledProps('folders'),
            shortcut: (
                <>
                    {shiftKey} {optionKey} F
                </>
            ),
            onSelect: () => {
                setOpen(false);
                setSelectedKey('createFolder');
                setCreationModalOpen(true);
            },
        },
        {
            id: 'createInvestment',
            label: 'Create Investment',
            icon: <MdAddChart size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'quickActions',
            ...createCommandDisabledProps('investments'),
            shortcut: <>{optionKey} + I</>,
            onSelect: () => {
                setOpen(false);
                setSelectedKey('createInvestment');
                setCreationModalOpen(true);
            },
        },
        {
            id: 'createMemo',
            label: 'Create Memo',
            icon: <TbFilePlus size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'quickActions',
            ...createCommandDisabledProps('memos'),
            shortcut: <>{optionKey} + M</>,
            onSelect: () => {
                setOpen(false);
                createMemo({ title: 'Untitled' }).then((m) => {
                    toast('Memo Created', {
                        icon: <TbFilePlus size='1rem' />,
                        description: m?.title,
                    });

                    navigate(`/memos/${m?.id}`);
                });
            },
        },
        {
            id: 'createPipeline',
            label: 'Create Pipeline',
            icon: <TbPlus size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'quickActions',
            ...createCommandDisabledProps('pipelines'),
            shortcut: <>{optionKey} + P</>,
            onSelect: () => {
                setOpen(false);
                setSelectedKey('createPipeline');
                setCreationModalOpen(true);
            },
        },
        // Imported Entities remain dormant until the product surface is relaunched.
        relationsEnabled && {
            id: 'createDashboard',
            label: 'Create Dashboard',
            icon: <TbPlus size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'quickActions',
            ...createCommandDisabledProps('dashboards'),
            shortcut: <>{optionKey} + D</>,
            onSelect: () => {
                setOpen(false);
                setSelectedKey('createDashboard');
                setCreationModalOpen(true);
            },
        },
        relationsEnabled && {
            id: 'createList',
            label: 'Create List',
            icon: <TbPlus size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'quickActions',
            ...createCommandDisabledProps('lists'),
            shortcut: <>{optionKey} + L</>,
            onSelect: () => {
                setOpen(false);
                setSelectedKey('createList');
                setCreationModalOpen(true);
            },
        },
        relationsEnabled && {
            id: 'createTask',
            label: 'Create Task',
            icon: <TbSquareRoundedPlus2 size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'quickActions',
            shortcut: <>{globalCreationShortcuts.tasks.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                setSelectedKey('createTask');
                setCreationModalOpen(true);
            },
        },
        {
            id: 'createTeam',
            label: 'Create Team',
            icon: <TbUsersPlus size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'quickActions',
            ...createCommandDisabledProps('teams'),
            shortcut: <>{optionKey} + T</>,
            onSelect: () => {
                setOpen(false);
                setSelectedKey('createTeam');
                setCreationModalOpen(true);
            },
        },

        // Account
        {
            id: 'accountSettings',
            label: 'Account Settings',
            icon: <TbUserCog size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'account',
            shortcut: <>{navigationRouteShortcuts.settings.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);
                navigate(navigationRouteShortcuts.settings.path);
            },
        },
        {
            id: 'logOut',
            label: 'Log Out',
            icon: <TbLogout size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'account',
            shortcut: (
                <>
                    {optionKey} + {shiftKey} + Q
                </>
            ),
            onSelect: () => {
                setOpen(false);

                Promise.all([localStorage.clear()]).then(() => {
                    window.location.href = '/auth/logout';
                });
            },
        },

        // Workspace
        {
            id: 'drafts',
            label: 'Drafts',
            icon: <TbPencil size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'workspace',
            shortcut: <>{navigationRouteShortcuts.drafts.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);

                navigate(navigationRouteShortcuts.drafts.path);
            },
        },
        {
            id: 'archive',
            label: 'Archive',
            icon: <TbArchive size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'workspace',
            shortcut: <>{navigationRouteShortcuts.archive.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);

                navigate(navigationRouteShortcuts.archive.path);
            },
        },
        {
            id: 'trash',
            label: 'Trash',
            icon: <TbTrash size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'workspace',
            shortcut: <>{navigationRouteShortcuts.trash.displayShortcut}</>,
            onSelect: () => {
                setOpen(false);

                navigate(navigationRouteShortcuts.trash.path);
            },
        },
        {
            id: 'workspaceSettings',
            label: 'Workspace Settings',
            icon: (
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='1rem'
                    height='1rem'
                    style={{ marginRight: '0.375rem' }}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-building-cog'
                >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M3 21h9' />
                    <path d='M9 8h1' />
                    <path d='M9 12h1' />
                    <path d='M9 16h1' />
                    <path d='M14 8h1' />
                    <path d='M14 12h1' />
                    <path d='M5 21v-16c0 -.53 .211 -1.039 .586 -1.414c.375 -.375 .884 -.586 1.414 -.586h10c.53 0 1.039 .211 1.414 .586c.375 .375 .586 .884 .586 1.414v7' />
                    <path d='M16 18c0 .53 .211 1.039 .586 1.414c.375 .375 .884 .586 1.414 .586c.53 0 1.039 -.211 1.414 -.586c.375 -.375 .586 -.884 .586 -1.414c0 -.53 -.211 -1.039 -.586 -1.414c-.375 -.375 -.884 -.586 -1.414 -.586c-.53 0 -1.039 .211 -1.414 .586c-.375 .375 -.586 .884 -.586 1.414z' />
                    <path d='M18 14.5v1.5' />
                    <path d='M18 20v1.5' />
                    <path d='M21.032 16.25l-1.299 .75' />
                    <path d='M16.27 19l-1.3 .75' />
                    <path d='M14.97 16.25l1.3 .75' />
                    <path d='M19.733 19l1.3 .75' />
                </svg>
            ),
            group: 'workspace',
            onSelect: () => {
                // setSelectedKey(e?.key);
                setOpen(false);

                navigate('/settings/workspace/general');
            },
        },
        {
            id: 'inviteToWorkspace',
            label: 'Invite to Workspace',
            icon: <TbUserPlus size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'workspace',
            disabled: !canInviteUsers(),
            disabledReason: 'Insufficient workspace permissions',
            shortcut: (
                <>
                    {optionKey} + {shiftKey} + M
                </>
            ),
            onSelect: () => {
                if (!canInviteUsers()) {
                    return;
                }

                setOpen(false);
                setSelectedKey('inviteToWorkspace');
                setCreationModalOpen(true);
            },
        },

        // Help
        {
            id: 'website',
            label: 'Website',
            icon: <TbWorldWww size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'help',
            onSelect: () => window.open('https://countercyclical.com', '_blank'),
            externalLink: true,
        },
        {
            id: 'documentation',
            label: 'Documentation',
            icon: <TbBook2 size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'help',
            onSelect: () => window.open('https://docs.countercyclical.io', '_blank'),
            externalLink: true,
        },
        {
            id: 'changelog',
            label: `What's New`,
            icon: <TbGift size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'help',
            onSelect: () => window.open('https://countercyclical.canny.io/changelog', '_blank'),
            externalLink: true,
        },
        {
            id: 'feedback',
            label: 'Send Feedback',
            icon: <TbSpeakerphone size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'help',
            onSelect: () => openNotionFormModal('feedback'),
        },
        {
            id: 'support',
            label: 'Support',
            icon: <TbLifebuoy size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'help',
            onSelect: () => openNotionFormModal('support'),
        },
        {
            id: 'privacy',
            label: 'Privacy Policy',
            icon: <TbFileText size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'help',
            onSelect: () => window.open('https://countercyclical.com/privacy', '_blank'),
            externalLink: true,
        },
        {
            id: 'tos',
            label: 'Terms of Service',
            icon: <TbFileText size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'help',
            onSelect: () => window.open('https://countercyclical.com/tos', '_blank'),
            externalLink: true,
        },

        // Miscellaneous
        {
            id: 'copyLinkURL',
            label: 'Copy Current Link',
            icon: <TbLink size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'miscellaneous',
            onSelect: () => {
                navigator.clipboard.writeText(window.location.href).then(() => {
                    toast('Link Copied', {
                        icon: <TbLink size='1rem' />,
                        description: window.location.href,
                    });
                    setOpen(false);
                });
            },
        },
        {
            id: 'theme',
            label: 'Change Theme',
            icon: <TbPalette size='1rem' style={{ marginRight: '0.375rem' }} />,
            group: 'miscellaneous',
            disabled: !canChangeTheme,
            disabledReason: 'This workspace currently requires its workspace theme.',
            shortcut: (
                <>
                    {currentTheme?.label ?? themeName}
                    {canChangeTheme && (
                        <>
                            <TbArrowRight style={{ verticalAlign: '-0.1rem', margin: '0 0.25rem' }} />
                            {nextTheme?.label}
                        </>
                    )}
                </>
            ),
            onSelect: () => {
                if (!nextTheme || !canChangeTheme) {
                    return;
                }

                setOpen(false);

                setTheme(nextTheme.name);

                toast('Theme Updated', {
                    icon: <TbPalette size='1rem' />,
                    description: (
                        <>
                            {currentTheme?.label ?? themeName}
                            <TbArrowRight style={{ verticalAlign: '-0.125rem', margin: '0 0.25rem' }} />
                            {nextTheme.label}
                        </>
                    ),
                });
            },
        },
    ];

    return commands.filter((command): command is Command => Boolean(command));
}
